  const API_BASE_URL = import.meta.env.VITE_API_URL?.replace(/\/$/, "");
import React, { useState, useEffect } from 'react';
import type { FC, ChangeEvent, FormEvent } from 'react';

interface PasswordState {
  current: string;
  new: string;
  confirm: string;
}

const ChangePasswordSection: FC = () => {
  const [passwords, setPasswords] = useState<PasswordState>({
    current: '',
    new: '',
    confirm: '',
  });
  const [error, setError] = useState<string>('');
  const [success, setSuccess] = useState<string>('');

  // Effect to clear success message after a few seconds
  useEffect(() => {
    if (success) {
      const timer = setTimeout(() => {
        setSuccess('');
      }, 3000); // Clear after 3 seconds
      return () => clearTimeout(timer);
    }
  }, [success]);

  // Effect to clear error message after a few seconds
  useEffect(() => {
    if (error) {
      const timer = setTimeout(() => {
        setError('');
      }, 3000); // Clear after 3 seconds
      return () => clearTimeout(timer);
    }
  }, [error]);

  const handleInputChange = (e: ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setPasswords(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    if (passwords.new !== passwords.confirm) {
      setError('New passwords do not match.');
      return;
    }
    if (passwords.new.length < 8) {
      setError('New password must be at least 8 characters long.');
      return;
    }
    try {
  const res = await fetch(`${API_BASE_URL}/api/auth/change-password`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          currentPassword: passwords.current,
          newPassword: passwords.new
        })
      });

      const data = await res.json();

      if (!res.ok || !data.success) {
        setError(data.message || 'Failed to update password.');
        return;
      }

      setSuccess('Password updated successfully!');
      setPasswords({ current: '', new: '', confirm: '' });
    } catch (err) {
      setError('Network error. Please try again.');
    }
  };

  const fields: { key: keyof PasswordState; label: string }[] = [
    { key: 'current', label: 'Current Password' },
    { key: 'new', label: 'New Password' },
    { key: 'confirm', label: 'Confirm Password' },
  ];

  return (
    <div className="max-w-2xl mx-auto px-4 sm:px-0">
      <h1 className="text-2xl sm:text-3xl font-bold text-gray-800 mb-6 sm:mb-8">Change Password</h1>

      <form onSubmit={handleSubmit} className="bg-white p-4 sm:p-6 lg:p-8 rounded-xl sm:rounded-2xl shadow-sm space-y-4 sm:space-y-6">
        {error && <div className="p-3 text-red-800 bg-red-100 rounded-lg">{error}</div>}
        {success && <div className="p-3 text-green-800 bg-green-100 rounded-lg">{success}</div>}

        {fields.map(field => (
          <div key={field.key}>
            <label htmlFor={field.key} className="block text-sm font-medium text-gray-700 mb-2">
              {field.label}
            </label>
            <input
              type="password"
              id={field.key}
              name={field.key}
              value={passwords[field.key]}
              onChange={handleInputChange}
              className="w-full px-4 py-2.5 border border-gray-200 rounded-xl  transition"
              required
            />
          </div>
        ))}

        <div>
          <button
            type="submit"
            className="w-full sm:w-auto bg-primary text-white px-8 py-3 rounded-xl hover:bg-primary/90 transition-colors font-semibold"
          >
            Update Password
          </button>
        </div>
      </form>
    </div>
  );
};

export default ChangePasswordSection;
